// src/modules/whatsapp-app/components/WhatsAppApp.tsx
//
// «WhatsApp» como app dentro de Orion (pedido de Samuel, 2026-10-22): se siente
// que estás dentro de WhatsApp revisando a tus clientes, pero el marco, la
// navegación y la tipografía son de Orion. Barra superior con el glyph, el
// número conectado y su estado, el modo de respuesta del agente y las cuatro
// pestañas de la app (22-oct, «todo lo de WhatsApp en su app»):
//   Chats · Difusión (contactos, plantillas, envíos masivos) ·
//   Números y conexión (números + «Conectar WhatsApp») · Configuración.
// Antes Difusión era el módulo suelto /m/whatsapp/* y la conexión estaba en
// Empresa → Configuración → WhatsApp; esas rutas redirigen aquí.
//
// 🔒 Todo lo decide Orión con la org del JWT (services/whatsapp_reply_mode.py,
// api/routes/whatsapp_app.py). Aquí solo se refleja; cambiar el modo lo
// permite el servidor solo a admin/cto.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, MessageSquareText, Settings, Smartphone } from 'lucide-react';

import { useIsMobile } from '@/components/ui/useIsMobile';
import {
  getNumberStatus, getOverview, MODE_LABEL, numberLabel, setReplyMode,
  type ReplyMode, type WaNumberStatus, type WaOverview,
} from '../api';
import { ChatsView } from './ChatsView';
import { DifusionView } from './DifusionView';
import { NumbersView } from './NumbersView';
import { SettingsView } from './SettingsView';
import { ModeControl, prettyPhone } from './shared';
import { WhatsAppGlyph } from './WhatsAppGlyph';
import './wa-app.css';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string; duration?: number }) => void;

export type WaView = 'chats' | 'difusion' | 'numeros' | 'ajustes';

/** Pestañas de la app, en orden. `key` = segmento de URL (/m/whatsapp-app/<key>);
 *  `ajustes` se conserva como URL de Configuración para no romper enlaces. */
export const WA_TABS: { key: WaView; label: string; short: string; Icon: typeof Settings }[] = [
  { key: 'chats', label: 'Chats', short: 'Chats', Icon: MessageSquareText },
  { key: 'difusion', label: 'Difusión', short: 'Difusión', Icon: Megaphone },
  { key: 'numeros', label: 'Números y conexión', short: 'Números', Icon: Smartphone },
  { key: 'ajustes', label: 'Configuración', short: 'Ajustes', Icon: Settings },
];

export function WhatsAppApp({ view = 'chats', addNotification }: { view?: WaView; addNotification?: Notifier }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);
  const [overview, setOverview] = useState<WaOverview | null>(null);
  const [ovError, setOvError] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<Record<string, WaNumberStatus | undefined>>({});
  const [modeBusy, setModeBusy] = useState(false);
  const [toast, setToast] = useState<{ type: string; title: string; message?: string } | null>(null);

  const notify = useCallback((type: 'success' | 'error' | 'info', title: string, message?: string) => {
    if (addNotification) addNotification({ type, title, message, duration: 4000 });
    else { setToast({ type, title, message }); window.setTimeout(() => setToast(null), 4000); }
  }, [addNotification]);

  const loadOverview = useCallback(() => {
    getOverview().then((o) => { setOverview(o); setOvError(null); })
      .catch((e) => setOvError(e?.message || 'No se pudo cargar WhatsApp'));
  }, []);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  // Estado en vivo de los números de Meta (caché de 10 min en el servidor).
  useEffect(() => {
    (overview?.numbers || []).filter((n) => n.enabled).forEach((n) => {
      if (statuses[n.key]) return;
      getNumberStatus(n.key).then((s) => setStatuses((x) => ({ ...x, [n.key]: s }))).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview?.numbers.map((n) => n.key).join(',')]);

  const main = useMemo(() => {
    const nums = (overview?.numbers || []).filter((n) => n.enabled);
    return nums.find((n) => n.is_default) || nums[0] || null;
  }, [overview]);
  const mainLive = main ? statuses[main.key] : undefined;
  const mainLabel = main ? prettyPhone(numberLabel(main, mainLive)) : '';
  const extra = (overview?.numbers || []).filter((n) => n.enabled).length - 1;
  const conn: 'ok' | 'warn' | 'off' = !overview ? 'warn' : !overview.connected ? 'off'
    : (mainLive && (mainLive.error || mainLive.live === false) && main?.provider === 'meta') ? 'warn' : 'ok';
  const ownModes = (overview?.numbers || []).filter((n) => n.reply_mode).length;

  const changeMode = async (m: ReplyMode) => {
    if (!overview?.can_edit) return;
    setModeBusy(true);
    try {
      const o = await setReplyMode(m);
      setOverview({ ...o, can_edit: true });
      notify('success', `Respuestas: ${MODE_LABEL[m]}`, m === 'suggest'
        ? 'El agente te dejará cada respuesta como sugerencia.'
        : m === 'off' ? 'El agente ya no responde; contesta tu equipo.' : 'El agente vuelve a responder solo.');
    } catch (e: any) {
      notify('error', 'No se cambió el modo', e?.message);
    } finally {
      setModeBusy(false);
    }
  };

  const tabBtn = ({ key, label, short, Icon }: (typeof WA_TABS)[number]) => (
    <button
      key={key}
      type="button"
      onClick={() => navigate(`/m/whatsapp-app/${key}`)}
      aria-current={view === key ? 'page' : undefined}
      title={label}
      className={[
        isMobile
          ? 'flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1.5 text-[11.5px]'
          : 'inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px]',
        'font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))]',
        view === key ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
      ].join(' ')}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      <span className={isMobile ? 'max-w-full truncate' : ''}>{isMobile ? short : label}</span>
    </button>
  );

  return (
    <div className="wa-app flex w-full flex-col text-foreground" style={{ height: isMobile ? 'calc(100dvh - 72px)' : 'calc(100dvh - 150px)', minHeight: 520 }}>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        {/* Barra superior de la app */}
        <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-3 py-2.5 sm:px-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-muted">
              <WhatsAppGlyph size={22} />
            </span>
            <div className="min-w-0">
              <h1 className="text-[16px] font-semibold leading-tight">WhatsApp</h1>
              <p className="flex items-center gap-1.5 truncate text-[12px] text-muted-foreground">
                <span className={`h-2 w-2 shrink-0 rounded-full ${conn === 'ok' ? 'bg-emerald-500' : conn === 'warn' ? 'bg-amber-500' : 'bg-muted-foreground'}`} aria-hidden />
                <span className="sr-only">{conn === 'ok' ? 'Conectado' : conn === 'warn' ? 'Revisar conexión' : 'Sin número conectado'}:</span>
                <span className="truncate tabular-nums">
                  {!overview ? (ovError || 'Cargando…') : main ? mainLabel : 'Sin número conectado'}
                  {extra > 0 && ` · +${extra} número${extra > 1 ? 's' : ''}`}
                </span>
              </p>
            </div>
          </div>

          <nav aria-label="Secciones de WhatsApp"
            className={isMobile
              ? 'order-3 grid w-full grid-cols-4 gap-1'
              : 'flex items-center gap-1 sm:order-3'}>
            {WA_TABS.map(tabBtn)}
          </nav>

          <div className="flex w-full items-center gap-2 sm:order-2 sm:ml-auto sm:w-auto">
            {overview && (
              <ModeControl
                value={overview.org_mode}
                onChange={changeMode}
                busy={modeBusy}
                disabled={!overview.can_edit || modeBusy}
                disabledReason="Solo administración puede cambiar el modo"
              />
            )}
            {ownModes > 0 && <span className="hidden xl:inline text-[11.5px] text-muted-foreground">{ownModes} número{ownModes > 1 ? 's' : ''} con modo propio</span>}
          </div>
        </header>

        <div className="min-h-0 flex-1">
          {view === 'ajustes' ? (
            <SettingsView overview={overview} onOverview={setOverview} statuses={statuses} notify={notify} isMobile={isMobile} />
          ) : view === 'difusion' ? (
            <DifusionView notify={notify} isMobile={isMobile} />
          ) : view === 'numeros' ? (
            <NumbersView overview={overview} statuses={statuses} notify={notify} onConnected={loadOverview} />
          ) : (
            <ChatsView overview={overview} isMobile={isMobile} notify={notify} numberLabel={mainLabel} />
          )}
        </div>
      </div>

      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex justify-center px-4">
        {toast && (
          <div role="status" className={`pointer-events-auto max-w-md rounded-xl border px-4 py-2.5 text-[13.5px] shadow-lg ${toast.type === 'error' ? 'border-destructive/50 bg-card text-destructive' : 'border-border bg-card text-foreground'}`}>
            <strong className="font-semibold">{toast.title}</strong>{toast.message ? <span className="text-muted-foreground"> · {toast.message}</span> : null}
          </div>
        )}
      </div>
    </div>
  );
}
